/**
 * Hashtag → Syphon Threshold Filter Config (STORY-008)
 *
 * Single source of truth mapping each curated hashtag to the set of
 * threshold conditions applied to its Syphon scan responses.
 *
 * Business edits — threshold values, or switching a condition between
 * existing operators — touch this file only. Adding a NEW operator kind also
 * needs a case in hashtagFilterEngine.js's evaluator switch (plus an entry in
 * VALID_OPERATORS below).
 *
 * Condition shape:
 *   { scanName: string, operator: Operator, value: number | string | string[] }
 *
 * Operators:
 *   '>=' | '<=' | '>' | '<' | '='  — numeric comparison against `filterValue`
 *   'inCodeSet'                    — string membership against value: string[]
 *   'notInCodeSet'                 — string non-membership against value: string[]
 *   'stringEquals'                 — case-sensitive string equality
 *   'stringContains'               — case-sensitive substring match
 *   'crossoverWithinDays'          — reads `filterValue.CrossoverDaysAgo`, matches <= value
 *
 * Thresholds are stored in the SAME units the Syphon `filterValue` uses,
 * so the evaluator performs no unit conversion:
 *   - MarketCap:         億円 (¥100M)          — e.g. `>= 5000` means ¥500B
 *   - InvestmentAmount:  raw yen               — e.g. `<= 5000`
 *   - Percent scans:     whole-number percent  — e.g. `>= 15` = 15%
 *   - Turnover scans:    倍 (times)            — e.g. `>= 30` = 30 times
 *   - Sector:            numeric sector code as string (e.g. '3050')
 *   - Exchange:          compound '<Listed_Exchange>,<SectionClassCode>'
 *                        e.g. TSE Prime = '166,0111'|'166,0113' (see
 *                        ScannerCategory Options patterns).
 *   - SecurityType:      SectionClassCode string (e.g. '0111', '0115').
 *                        Common-stock = NOT in {'0115','0116','0119'}
 *                        (ETF/REIT codes).
 *   - IchimokuCloud:     'Above' | 'Within' | 'Below'
 *   - SMACrossover_5_25: derived `CrossoverDaysAgo` (number of days)
 *
 * @see STORY-008 spec: specs/story-008-syphon-filtering/spec.md
 * @see STORY-007 map:  src/constants/hashtagScanMap.js
 */

/**
 * Whitelist of every operator kind accepted by the filter engine.
 * @type {ReadonlyArray<string>}
 */
export const VALID_OPERATORS = Object.freeze([
  '>=',
  '<=',
  '>',
  '<',
  '=',
  'inCodeSet',
  'notInCodeSet',
  'stringEquals',
  'stringContains',
  'crossoverWithinDays'
]);

/**
 * TSE Prime Exchange codes (Syphon filterValue is
 * `<Listed_Exchange>,<SectionClassCode>` for the `Exchange` scan).
 * Sourced from ScannerCategory `Exchange.Options.TSE_P.Pattern`.
 * @type {ReadonlyArray<string>}
 */
export const EXCHANGE_CODES_TSE_PRIME = Object.freeze(['166,0111', '166,0113']);

/**
 * TSE Growth Exchange codes.
 * Sourced from ScannerCategory `Exchange.Options.TSE_G.Pattern`.
 * @type {ReadonlyArray<string>}
 */
export const EXCHANGE_CODES_TSE_GROWTH = Object.freeze([
  '166,0114',
  '166,0118'
]);

/**
 * ETF + REIT SectionClassCodes — used by hashtag #4 to EXCLUDE ETFs & REITs
 * (common stock = 普通株 = NOT in this set).
 * Sourced from ScannerCategory `SecurityType.Options.Equity.Pattern`
 * (`^(?!0115|0116|0119).*$`).
 * @type {ReadonlyArray<string>}
 */
export const SECURITY_TYPE_CODES_NON_EQUITY = Object.freeze([
  '0115', // ETF
  '0116', // ETF
  '0119' // REIT
]);

/**
 * BtoC sector codes used by hashtag #1 (#誰もが知る身近な企業).
 * Confirmed source: STORY-007 §"Hashtag #1 sector codes (reference)".
 * @type {ReadonlyArray<string>}
 */
export const SECTOR_CODES_HASHTAG_1 = Object.freeze([
  '3050', // 食料品 (Foods)
  '3250', // 医薬品 (Pharmaceutical)
  '3650', // 電気機器 (Electric Appliances)
  '3700', // 輸送用機器 (Transportation Equipment)
  '3800', // その他製品 (Other Products)
  '4050', // 電気・ガス業 (Electric Power & Gas)
  '5250', // 情報・通信業 (Information & Communication)
  '6100', // 小売業 (Retail Trade)
  '9050' // サービス業 (Services)
]);

/**
 * Recursively freezes an object and everything reachable from it, so the whole
 * config tree (arrays, condition objects, and value arrays) is immutable — not
 * just the top level, which a single `Object.freeze` would leave shallow.
 */
function deepFreeze(obj) {
  Object.values(obj).forEach((value) => {
    if (value && typeof value === 'object' && !Object.isFrozen(value)) {
      deepFreeze(value);
    }
  });
  return Object.freeze(obj);
}

/**
 * Threshold config keyed by hashtag ID.
 *
 * This is the single source of truth for which scans each hashtag needs:
 * HASHTAG_SCAN_MAP (src/constants/hashtagScanMap.js) is DERIVED from the
 * `scanName`s below, so the fetch list and the filter/loading gate cannot
 * drift out of sync. Adding/removing a condition here automatically updates
 * the scans that get fetched.
 *
 * Keys correspond to `id` in HASHTAG_DEFINITIONS (src/views/scan/HashtagChips.js).
 */
const HASHTAG_FILTER_CONFIG = {
  // #1 #誰もが知る身近な企業
  daily_life_companies: [
    {
      scanName: 'Sector',
      operator: 'inCodeSet',
      value: SECTOR_CODES_HASHTAG_1
    },
    { scanName: 'MarketCap', operator: '>=', value: 5000 }, // ¥500B in 億円
    {
      scanName: 'Exchange',
      operator: 'inCodeSet',
      value: EXCHANGE_CODES_TSE_PRIME
    }
  ],
  // #2 #高効率で稼ぐ優良企業
  elite_management: [
    { scanName: 'ReturnOnEquity', operator: '>=', value: 15 },
    { scanName: 'OrdinaryProfitToNetSalesRatio', operator: '>=', value: 10 },
    { scanName: 'TotalScore', operator: '>=', value: 60 }
  ],
  // #3 #配当に着目した高配当株
  passive_income: [
    { scanName: 'DividendYieldForecast', operator: '>=', value: 3.5 },
    { scanName: 'DividendPayoutRatio', operator: '<=', value: 30 }
  ],
  // #4 #手軽に投資！少額株
  affordable_stocks: [
    { scanName: 'InvestmentAmount', operator: '<=', value: 5000 }, // raw yen
    {
      scanName: 'SecurityType',
      operator: 'notInCodeSet',
      value: SECURITY_TYPE_CODES_NON_EQUITY
    }
  ],
  // #5 #次世代の主役を探せ
  bonus_adventure: [
    {
      scanName: 'Exchange',
      operator: 'inCodeSet',
      value: EXCHANGE_CODES_TSE_GROWTH
    },
    { scanName: 'NetSalesChange', operator: '>=', value: 20 },
    { scanName: 'GrowthScore', operator: '>=', value: 70 }
  ],
  // #6 #下げ止まりからの反発狙い
  counterattack_from_bottom: [
    { scanName: 'StochasticFast_9', operator: '<=', value: 20 },
    { scanName: 'IchimokuCloud', operator: 'stringEquals', value: 'Above' }
  ],
  // #7 #高成長期待の小型株
  potential_big_breakout: [
    { scanName: 'MarketCap', operator: '<=', value: 300 }, // ¥30B in 億円
    { scanName: 'ReturnOnEquity', operator: '>=', value: 15 },
    { scanName: 'NetSalesChange', operator: '>=', value: 15 },
    { scanName: 'GrowthScore', operator: '>=', value: 70 }
  ],
  // #8 #プロの想定価格より割安
  far_from_target_price: [
    { scanName: 'TargetPriceVsCurrentPrice', operator: '>=', value: 30 },
    { scanName: 'AnalystCount', operator: '>=', value: 5 }
  ],
  // #9 #プロ注目株
  pro_approved_attention: [
    { scanName: 'Rating', operator: '>=', value: 1.5 },
    { scanName: 'AnalystCount', operator: '>=', value: 5 }
  ],
  // #10 #今が狙い目の割安株
  treasure_hunting_undervalued: [
    { scanName: 'PriceCashFlowRatio', operator: '<=', value: 0.99 },
    { scanName: 'SMADeviation_25', operator: '<=', value: -10 },
    { scanName: 'PredictedOrdinaryProfit', operator: '>=', value: 0 }
  ],
  // #11 #上昇トレンド継続中
  unstoppable_momentum: [
    {
      scanName: 'SMACrossover_5_25',
      operator: 'crossoverWithinDays',
      value: 10
    },
    { scanName: 'SMADeviation_25', operator: '>=', value: 0 }
  ],
  // #12 #無駄なく稼ぐ効率重視企業
  fast_inventory_capital_cycling: [
    { scanName: 'InventoryTurnoverRate', operator: '>=', value: 30 }, // times (倍)
    { scanName: 'ReceivablesTurnoverRate', operator: '>=', value: 30 }
  ]
};

export default deepFreeze(HASHTAG_FILTER_CONFIG);
